import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { trackAddToCart } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const load = () => {
  try { return JSON.parse(localStorage.getItem("eh_cart")) || {}; }
  catch { return {}; }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load); // { herb_id: qty }
  const { isAuthenticated } = useAuth();
  const syncTimer = useRef(null);

  useEffect(() => {
    localStorage.setItem("eh_cart", JSON.stringify(items));
  }, [items]);

  // Debounced server snapshot for abandoned-cart recovery emails
  useEffect(() => {
    if (!isAuthenticated) return;
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      axios.post(`${API}/store/cart-snapshot`, {
        items: Object.entries(items).map(([herb_id, quantity]) => ({ herb_id, quantity })),
      }).catch(() => {});
    }, 2000);
    return () => clearTimeout(syncTimer.current);
  }, [items, isAuthenticated]);

  const addItem = useCallback((herbId, qty = 1) => {
    trackAddToCart(herbId);
    setItems((c) => ({ ...c, [herbId]: Math.min((c[herbId] || 0) + qty, 20) }));
  }, []);

  const setQty = useCallback((herbId, qty) => {
    setItems((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[herbId];
      else next[herbId] = Math.min(qty, 20);
      return next;
    });
  }, []);

  const removeItem = useCallback((herbId) => setQty(herbId, 0), [setQty]);
  const clearCart = useCallback(() => setItems({}), []);
  const count = Object.values(items).reduce((a, b) => a + b, 0);

  return (
    <CartContext.Provider value={{ items, addItem, setQty, removeItem, clearCart, count }}>
      {children}
    </CartContext.Provider>
  );
};
